import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Search,
  Download,
  Edit,
  Trash2,
  Clock,
  CheckCircle,
  AlertTriangle,
  Pause,
  Play,
  X,
  Settings,
  Building2,
  Wrench,
  Users,
  Bell,
  History,
  RefreshCw,
  Zap,
  Info,
  XCircle,
  FileSpreadsheet,
  FileText,
  ChevronDown
} from 'lucide-react';
import { pmScheduleService } from '../../../services/pmScheduleService';
import { assetService } from '../../../services/assetService';
import { contractService } from '../../../services/contractService';
import { exportToCSV, exportToExcel, getDateString } from '../../../utils/exportUtils';
import { useDateFormat } from '../../../hooks/useDateFormat';

// Toast Notification Component
const Toast = ({ toasts, removeToast }) => {
  return (
    <div className="fixed top-4 right-4 z-[100] space-y-2">
      {toasts.map((toast) => {
        const icons = {
          success: <CheckCircle className="w-5 h-5 text-green-500" />,
          error: <XCircle className="w-5 h-5 text-red-500" />,
          info: <Info className="w-5 h-5 text-blue-500" />,
          warning: <AlertTriangle className="w-5 h-5 text-yellow-500" />
        };
        const bgColors = {
          success: 'bg-green-50 border-green-200',
          error: 'bg-red-50 border-red-200',
          info: 'bg-blue-50 border-blue-200',
          warning: 'bg-yellow-50 border-yellow-200'
        };
        return (
          <div
            key={toast.id}
            className={`flex items-start gap-3 p-4 rounded-lg border shadow-lg ${bgColors[toast.type]} animate-slide-in min-w-[320px] max-w-md`}
          >
            {icons[toast.type]}
            <p className="flex-1 text-sm text-gray-700">{toast.message}</p>
            <button
              onClick={() => removeToast(toast.id)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

// Confirmation Modal Component
const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, confirmText, confirmStyle = 'primary', icon: Icon = AlertTriangle }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const buttonStyles = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white',
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    warning: 'bg-orange-600 hover:bg-orange-700 text-white'
  };

  const iconStyles = {
    primary: 'bg-blue-100 text-blue-600',
    danger: 'bg-red-100 text-red-600',
    warning: 'bg-orange-100 text-orange-600'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden animate-scale-in">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-full ${iconStyles[confirmStyle]}`}>
              <Icon className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <p className="mt-2 text-sm text-gray-600">{message}</p>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={() => { onConfirm(); onClose(); }}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${buttonStyles[confirmStyle]}`}
          >
            {confirmText || t('common.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Custom hook for toast notifications
const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return { toasts, addToast, removeToast };
};

// Custom hook for confirmation modal
const useConfirm = () => {
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    confirmStyle: 'primary',
    icon: AlertTriangle,
    onConfirm: () => {},
    onCancel: () => {}
  });

  const confirm = useCallback((options) => {
    return new Promise((resolve, reject) => {
      setConfirmState({
        isOpen: true,
        title: options.title || 'Confirm',
        message: options.message || 'Are you sure?',
        confirmText: options.confirmText || 'Confirm',
        confirmStyle: options.confirmStyle || 'primary',
        icon: options.icon || AlertTriangle,
        onConfirm: () => resolve(true),
        onCancel: () => reject(new Error('cancelled'))
      });
    });
  }, []);

  const closeConfirm = useCallback(() => {
    confirmState.onCancel();
    setConfirmState(prev => ({ ...prev, isOpen: false }));
  }, [confirmState]);

  return { confirmState, confirm, closeConfirm };
};

const PMSchedules = () => {
  const { t } = useTranslation();
  const [schedules, setSchedules] = useState([]);
  const [assets, setAssets] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [processingDue, setProcessingDue] = useState(false);

  // Toast and Confirm hooks
  const { toasts, addToast, removeToast } = useToast();
  const { confirmState, confirm, closeConfirm } = useConfirm();
  const [showExportMenu, setShowExportMenu] = useState(false);
  const { formatDate } = useDateFormat();

  // Export handler
  const handleExport = (format) => {
    const data = schedules.map(s => ({
      name: s.name || '',
      assetName: s.assetName || '',
      maintenanceType: s.maintenanceType || '',
      frequencyDisplay: s.frequencyDisplay || '',
      priority: s.priority || '',
      status: s.status || '',
      nextDueDate: s.nextDueDate || '',
      lastPerformedDate: s.lastPerformedDate || '',
      supplierName: s.supplierName || '',
      assignedUserName: s.assignedUserName || '',
      isOverdue: s.isOverdue ? 'Yes' : 'No',
      daysUntilDue: s.daysUntilDue || 0,
    }));

    const columns = [
      { key: 'name', label: 'Schedule Name' },
      { key: 'assetName', label: 'Asset' },
      { key: 'maintenanceType', label: 'Type' },
      { key: 'frequencyDisplay', label: 'Frequency' },
      { key: 'priority', label: 'Priority' },
      { key: 'status', label: 'Status' },
      { key: 'nextDueDate', label: 'Next Due Date' },
      { key: 'lastPerformedDate', label: 'Last Performed' },
      { key: 'supplierName', label: 'Supplier' },
      { key: 'assignedUserName', label: 'Assigned To' },
      { key: 'isOverdue', label: 'Overdue' },
      { key: 'daysUntilDue', label: 'Days Until Due' },
    ];

    const filename = `pm-schedules-${getDateString()}`;
    if (format === 'csv') {
      exportToCSV(data, columns, filename);
    } else {
      exportToExcel(data, columns, filename);
    }
    setShowExportMenu(false);
  };

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    assetId: '',
    maintenanceType: 'INTERNAL',
    contractId: '',
    assignedUserId: '',
    frequencyType: 'MONTHS',
    frequencyValue: 1,
    taskDescription: '',
    estimatedDurationHours: '',
    priority: 'MEDIUM',
    startDate: '',
    endDate: '',
    leadDays: 1,
    notifyEmails: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [schedulesData, assetsData, contractsData] = await Promise.all([
        pmScheduleService.getSchedules().catch(() => []),
        assetService.getAssets().catch(() => []),
        contractService.getContracts().catch(() => [])
      ]);
      setSchedules(schedulesData || []);
      setAssets(assetsData || []);
      setContracts(contractsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (schedule) => {
    if (schedule.status === 'PAUSED') {
      return { bg: 'bg-gray-100 text-gray-800', icon: Pause, label: t('cmms.pm_paused') };
    }
    if (schedule.isOverdue) {
      return { bg: 'bg-red-100 text-red-800', icon: AlertTriangle, label: t('cmms.pm_overdue') };
    }
    if (schedule.daysUntilDue <= 7) {
      return { bg: 'bg-yellow-100 text-yellow-800', icon: Clock, label: t('cmms.pm_dueSoon') };
    }
    return { bg: 'bg-green-100 text-green-800', icon: CheckCircle, label: t('common.active') };
  };

  const getTypeBadge = (type) => {
    return type === 'EXTERNAL'
      ? { bg: 'bg-blue-100 text-blue-800', label: t('cmms.pm_external') }
      : { bg: 'bg-purple-100 text-purple-800', label: t('cmms.pm_internal') };
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      LOW: 'bg-gray-100 text-gray-700',
      MEDIUM: 'bg-blue-100 text-blue-700',
      HIGH: 'bg-orange-100 text-orange-700',
      CRITICAL: 'bg-red-100 text-red-700'
    };
    return styles[priority] || styles.MEDIUM;
  };

  const filteredSchedules = schedules.filter(schedule => {
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'overdue' && schedule.isOverdue) ||
      (filterStatus === 'active' && schedule.status === 'ACTIVE' && !schedule.isOverdue) ||
      (filterStatus === 'paused' && schedule.status === 'PAUSED');
    const matchesType = filterType === 'all' || schedule.maintenanceType === filterType;
    const matchesSearch =
      schedule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      schedule.assetName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      schedule.supplierName?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesType && matchesSearch;
  });

  // Handlers
  const handleCreate = () => {
    setEditingSchedule(null);
    setFormData({
      name: '',
      description: '',
      assetId: '',
      maintenanceType: 'INTERNAL',
      contractId: '',
      assignedUserId: '',
      frequencyType: 'MONTHS',
      frequencyValue: 1,
      taskDescription: '',
      estimatedDurationHours: '',
      priority: 'MEDIUM',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      leadDays: 1,
      notifyEmails: ''
    });
    setShowModal(true);
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      name: schedule.name,
      description: schedule.description || '',
      assetId: schedule.assetId,
      maintenanceType: schedule.maintenanceType,
      contractId: schedule.contractId || '',
      assignedUserId: schedule.assignedUserId || '',
      frequencyType: schedule.frequencyType,
      frequencyValue: schedule.frequencyValue,
      taskDescription: schedule.taskDescription || '',
      estimatedDurationHours: schedule.estimatedDurationHours || '',
      priority: schedule.priority,
      startDate: schedule.startDate || '',
      endDate: schedule.endDate || '',
      leadDays: schedule.leadDays,
      notifyEmails: schedule.notifyEmails || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        frequencyValue: parseInt(formData.frequencyValue),
        leadDays: parseInt(formData.leadDays),
        estimatedDurationHours: formData.estimatedDurationHours ? parseFloat(formData.estimatedDurationHours) : null,
        assetId: parseInt(formData.assetId),
        contractId: formData.contractId ? parseInt(formData.contractId) : null,
        assignedUserId: formData.assignedUserId ? parseInt(formData.assignedUserId) : null
      };

      if (editingSchedule) {
        await pmScheduleService.updateSchedule(editingSchedule.id, submitData);
        addToast('PM schedule updated successfully!', 'success');
      } else {
        await pmScheduleService.createSchedule(submitData);
        addToast('PM schedule created successfully!', 'success');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving PM schedule:', error);
      addToast('Failed to save PM schedule. Please try again.', 'error');
    }
  };

  const handleToggleStatus = async (schedule) => {
    try {
      const newStatus = schedule.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
      await pmScheduleService.updateScheduleStatus(schedule.id, newStatus);
      addToast(`PM schedule ${newStatus === 'PAUSED' ? 'paused' : 'activated'} successfully!`, 'success');
      loadData();
    } catch (error) {
      console.error('Error updating status:', error);
      addToast('Failed to update status. Please try again.', 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await confirm({
        title: t('cmms.pm_deleteTitle'),
        message: t('cmms.pm_deleteConfirm'),
        confirmText: t('common.delete'),
        confirmStyle: 'danger',
        icon: Trash2
      });
      await pmScheduleService.deleteSchedule(id);
      addToast('PM schedule deleted successfully!', 'success');
      loadData();
    } catch (error) {
      if (error.message === 'cancelled') return;
      console.error('Error deleting PM schedule:', error);
      addToast('Failed to delete PM schedule. Please try again.', 'error');
    }
  };

  const handleViewHistory = async (schedule) => {
    setSelectedSchedule(schedule);
    try {
      const history = await pmScheduleService.getScheduleHistory(schedule.id);
      setHistoryData(history || []);
      setShowHistoryModal(true);
    } catch (error) {
      console.error('Error loading history:', error);
      setHistoryData([]);
      setShowHistoryModal(true);
    }
  };

  const handleProcessDueSchedules = async () => {
    if (processingDue) return;

    const overdueCount = stats.overdue;
    if (overdueCount === 0) {
      addToast('No overdue PM schedules to process.', 'info');
      return;
    }

    try {
      await confirm({
        title: 'Process Overdue Schedules',
        message: `This will process ${overdueCount} overdue PM schedule(s) and generate work orders or visits for all due schedules. Continue?`,
        confirmText: 'Process All',
        confirmStyle: 'warning',
        icon: Zap
      });

      setProcessingDue(true);
      await pmScheduleService.processDueSchedules();
      addToast('Due PM schedules processed successfully! Work orders/visits have been generated.', 'success');
      loadData();
    } catch (error) {
      if (error.message === 'cancelled') return;
      console.error('Error processing due schedules:', error);
      addToast('Failed to process due schedules. Please try again.', 'error');
    } finally {
      setProcessingDue(false);
    }
  };

  const handleTriggerGeneration = async (schedule) => {
    const taskType = schedule.maintenanceType === 'INTERNAL' ? t('cmms.pm_workOrder') : t('cmms.pm_visit');

    try {
      await confirm({
        title: `Generate ${taskType}`,
        message: `Generate ${taskType} for "${schedule.name}" now? This will create a new maintenance task.`,
        confirmText: 'Generate',
        confirmStyle: 'primary',
        icon: Zap
      });

      const result = await pmScheduleService.triggerGeneration(schedule.id);
      if (result) {
        addToast(`${taskType} generated successfully!`, 'success');
      } else {
        addToast(`${taskType} already exists for this period.`, 'info');
      }
      loadData();
    } catch (error) {
      if (error.message === 'cancelled') return;
      console.error('Error triggering generation:', error);
      addToast(`Failed to generate ${taskType}. Please try again.`, 'error');
    }
  };

  // Stats
  const stats = {
    total: schedules.length,
    active: schedules.filter(s => s.status === 'ACTIVE' && !s.isOverdue).length,
    overdue: schedules.filter(s => s.isOverdue).length,
    dueSoon: schedules.filter(s => s.daysUntilDue <= 7 && s.daysUntilDue >= 0).length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('cmms.pmSchedules')}</h1>
          <p className="text-gray-600 mt-1">{t('cmms.pm_subtitle')}</p>
        </div>
        <div className="flex gap-3">
          {stats.overdue > 0 && (
            <button
              onClick={handleProcessDueSchedules}
              disabled={processingDue}
              className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processingDue ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  {t('cmms.pm_processing')}
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5 mr-2" />
                  {t('cmms.pm_processOverdue')} ({stats.overdue})
                </>
              )}
            </button>
          )}
          {/* Export Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
            >
              <Download className="w-5 h-5 mr-2" />
              {t('common.export')}
              <ChevronDown className="w-4 h-4 ml-2" />
            </button>
            {showExportMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowExportMenu(false)} />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                  <button
                    onClick={() => handleExport('excel')}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FileSpreadsheet className="w-4 h-4 text-green-600" />
                    {t('common.exportExcel')}
                  </button>
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4 text-blue-600" />
                    {t('common.exportCsv')}
                  </button>
                </div>
              </>
            )}
          </div>
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t('cmms.pm_newSchedule')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Settings className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-600">{t('common.total')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.active}</div>
              <div className="text-sm text-gray-600">{t('common.active')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.overdue}</div>
              <div className="text-sm text-gray-600">{t('cmms.pm_overdue')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.dueSoon}</div>
              <div className="text-sm text-gray-600">{t('cmms.pm_dueThisWeek')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('common.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('common.allTypes')}</option>
            <option value="INTERNAL">{t('cmms.pm_internal')}</option>
            <option value="EXTERNAL">{t('cmms.pm_external')}</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('common.allStatus')}</option>
            <option value="active">{t('common.active')}</option>
            <option value="overdue">{t('cmms.pm_overdue')}</option>
            <option value="paused">{t('cmms.pm_paused')}</option>
          </select>
          <button
            onClick={loadData}
            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
            title={t('common.refresh')}
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Schedule List */}
      <div className="space-y-4">
        {loading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">{t('common.loading')}</p>
          </div>
        )}

        {!loading && filteredSchedules.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <Settings className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('cmms.pm_noResults')}</h3>
            <p className="text-gray-500 mb-4">{t('cmms.pm_noResultsDesc')}</p>
            <button
              onClick={handleCreate}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('cmms.pm_newSchedule')}
            </button>
          </div>
        )}

        {filteredSchedules.map((schedule) => {
          const statusConfig = getStatusBadge(schedule);
          const typeConfig = getTypeBadge(schedule.maintenanceType);
          const StatusIcon = statusConfig.icon;

          return (
            <div key={schedule.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <Settings className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900">{schedule.name}</h3>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${statusConfig.bg}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.label}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${typeConfig.bg}`}>
                        {typeConfig.label}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getPriorityBadge(schedule.priority)}`}>
                        {schedule.priority}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Wrench className="w-4 h-4" />
                        {schedule.assetName}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {schedule.frequencyDisplay}
                      </span>
                      {schedule.maintenanceType === 'EXTERNAL' && schedule.supplierName && (
                        <span className="flex items-center gap-1">
                          <Building2 className="w-4 h-4" />
                          {schedule.supplierName}
                        </span>
                      )}
                      {schedule.maintenanceType === 'INTERNAL' && schedule.assignedUserName && (
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {schedule.assignedUserName}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm">
                      <span className="text-gray-500">
                        Next Due: <span className={schedule.isOverdue ? 'text-red-600 font-medium' : 'text-gray-900'}>
                          {formatDate(schedule.nextDueDate)}
                        </span>
                        {schedule.daysUntilDue !== undefined && (
                          <span className="ml-1 text-gray-400">
                            ({schedule.daysUntilDue < 0 ? `${Math.abs(schedule.daysUntilDue)} days overdue` :
                              schedule.daysUntilDue === 0 ? 'Today' :
                              `in ${schedule.daysUntilDue} days`})
                          </span>
                        )}
                      </span>
                      {schedule.lastPerformedDate && (
                        <span className="text-gray-500">
                          Last: {formatDate(schedule.lastPerformedDate)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {schedule.status === 'ACTIVE' && (schedule.isOverdue || schedule.daysUntilDue <= 0) && (
                    <button
                      onClick={() => handleTriggerGeneration(schedule)}
                      className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg"
                      title="Generate Work Order/Visit Now"
                    >
                      <Zap className="w-5 h-5" />
                    </button>
                  )}
                  <button
                    onClick={() => handleViewHistory(schedule)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                    title={t('common.view')}
                  >
                    <History className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleToggleStatus(schedule)}
                    className={`p-2 rounded-lg ${
                      schedule.status === 'ACTIVE'
                        ? 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50'
                        : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                    }`}
                    title={schedule.status === 'ACTIVE' ? 'Pause' : 'Activate'}
                  >
                    {schedule.status === 'ACTIVE' ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => handleEdit(schedule)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                    title={t('common.edit')}
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(schedule.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    title={t('common.delete')}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingSchedule ? t('common.edit') : t('common.create')}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.name')} *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.pm_asset')} *</label>
                  <select
                    value={formData.assetId}
                    onChange={(e) => setFormData({...formData, assetId: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">{t('cmms.pm_selectAsset')}</option>
                    {assets.map(asset => (
                      <option key={asset.id} value={asset.id}>{asset.name} ({asset.assetCode})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.pm_maintenanceType')} *</label>
                  <select
                    value={formData.maintenanceType}
                    onChange={(e) => setFormData({...formData, maintenanceType: e.target.value, contractId: '', assignedUserId: ''})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="INTERNAL">{t('cmms.pm_internalWO')}</option>
                    <option value="EXTERNAL">{t('cmms.pm_externalVisit')}</option>
                  </select>
                </div>
              </div>

              {formData.maintenanceType === 'EXTERNAL' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.pm_contract')} *</label>
                  <select
                    value={formData.contractId}
                    onChange={(e) => setFormData({...formData, contractId: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">{t('cmms.pm_selectContract')}</option>
                    {contracts.map(contract => (
                      <option key={contract.id} value={contract.id}>
                        {contract.contractNumber} - {contract.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.pm_frequency')} *</label>
                  <select
                    value={formData.frequencyType}
                    onChange={(e) => setFormData({...formData, frequencyType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="DAYS">{t('cmms.pm_days')}</option>
                    <option value="WEEKS">{t('cmms.pm_weeks')}</option>
                    <option value="MONTHS">{t('cmms.pm_months')}</option>
                    <option value="YEARS">{t('cmms.pm_years')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.pm_every')} *</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.frequencyValue}
                    onChange={(e) => setFormData({...formData, frequencyValue: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.priority')}</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="LOW">{t('cmms.wo_low')}</option>
                    <option value="MEDIUM">{t('cmms.wo_medium')}</option>
                    <option value="HIGH">{t('cmms.wo_high')}</option>
                    <option value="CRITICAL">{t('cmms.wo_urgent')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.pm_taskDescription')}</label>
                <textarea
                  value={formData.taskDescription}
                  onChange={(e) => setFormData({...formData, taskDescription: e.target.value})}
                  rows={3}
                  placeholder={t('cmms.pm_taskDescPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.pm_startDate')}</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.pm_endDate')}</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.pm_estimatedDuration')}</label>
                  <input
                    type="number"
                    step="0.5"
                    value={formData.estimatedDurationHours}
                    onChange={(e) => setFormData({...formData, estimatedDurationHours: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.pm_reminderDays')}</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.leadDays}
                    onChange={(e) => setFormData({...formData, leadDays: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Bell className="w-4 h-4 inline mr-1" />
                  {t('cmms.pm_notificationEmails')}
                </label>
                <input
                  type="text"
                  value={formData.notifyEmails}
                  onChange={(e) => setFormData({...formData, notifyEmails: e.target.value})}
                  placeholder="email1@example.com, email2@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">{t('cmms.pm_emailsHint')}</p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingSchedule ? t('common.update') : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && selectedSchedule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{t('cmms.pm_history')}</h2>
                <p className="text-sm text-gray-500 mt-1">{selectedSchedule.name}</p>
              </div>
              <button onClick={() => setShowHistoryModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              {historyData.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <History className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p>{t('cmms.pm_noHistory')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {historyData.map((item) => (
                    <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">
                              {formatDate(item.scheduledDate)}
                            </span>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              item.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                              item.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                              item.status === 'OVERDUE' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {item.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            {item.generatedType === 'INTERNAL' ? t('cmms.pm_workOrder') : t('cmms.pm_visit')}:
                            {' '}{item.generatedWorkOrderNumber || item.generatedVisitNumber || '-'}
                          </p>
                          {item.performedBy && (
                            <p className="text-sm text-gray-500">
                              {t('cmms.pm_performedBy')}: {item.performedBy} on {formatDate(item.performedDate)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <Toast toasts={toasts} removeToast={removeToast} />

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmState.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        confirmStyle={confirmState.confirmStyle}
        icon={confirmState.icon}
      />
    </div>
  );
};

export default PMSchedules;
